import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, CreditCard, Layers, FileText, BookOpen, 
  ArrowLeft, RefreshCw, Eye, Clock, ChevronRight,
  TrendingUp, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BTT Admin Dashboard - Admin interface for managing BTT subscribers, projects, and resources
 * Integrated into the existing CMS
 */
const BTTAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [applications, setApplications] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('session_token') || localStorage.getItem('auth_token');
    if (!token) {
      window.location.href = '/cms/login';
      return;
    }

    try {
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/btt/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      // Fetch subscribers
      const subsRes = await fetch(`${API_URL}/api/btt/admin/subscribers?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (subsRes.ok) {
        const data = await subsRes.json();
        setSubscribers(data);
      }

      // Fetch projects
      const projRes = await fetch(`${API_URL}/api/btt/admin/projects?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (projRes.ok) {
        const data = await projRes.json();
        setProjects(data);
      }

      // Fetch applications
      const appsRes = await fetch(`${API_URL}/api/applications?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (appsRes.ok) {
        const data = await appsRes.json();
        setApplications(data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00A86B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-black border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/cms" className="text-white/60 hover:text-white">
                <ArrowLeft size={20} />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white">BTT Admin Dashboard</h1>
                <p className="text-white/60 text-sm">Manage subscribers, projects, and resources</p>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-black/50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {[
              { id: 'overview', label: 'Overview', icon: TrendingUp },
              { id: 'subscribers', label: 'Subscribers', icon: Users },
              { id: 'projects', label: 'Projects', icon: Layers },
              { id: 'applications', label: 'Applications', icon: FileText }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#00A86B] text-[#00A86B]'
                    : 'border-transparent text-white/60 hover:text-white'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'overview' && (
          <OverviewSection stats={stats} subscribers={subscribers} projects={projects} applications={applications} />
        )}
        {activeTab === 'subscribers' && (
          <SubscribersSection subscribers={subscribers} onRefresh={fetchData} />
        )}
        {activeTab === 'projects' && (
          <ProjectsSection projects={projects} onRefresh={fetchData} />
        )}
        {activeTab === 'applications' && (
          <ApplicationsSection applications={applications} onRefresh={fetchData} />
        )}
      </div>
    </div>
  );
};

/**
 * Overview Section
 */
const OverviewSection = ({ stats, subscribers, projects, applications }) => {
  return (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <StatCard
          label="Active Subscribers"
          value={stats?.users?.active_subscriptions || 0}
          icon={CreditCard}
          color="green"
        />
        <StatCard
          label="Total Users"
          value={stats?.users?.total || 0}
          icon={Users}
          color="blue"
        />
        <StatCard
          label="Active Projects"
          value={stats?.projects?.active || 0}
          icon={Layers}
          color="purple"
        />
        <StatCard
          label="Pending Applications"
          value={stats?.applications?.pending || 0}
          icon={FileText}
          color="orange"
        />
      </div>

      {/* Recent Activity Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent Subscribers */}
        <div className="bg-zinc-900 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Users size={18} className="text-[#00A86B]" />
            Recent Subscribers
          </h3>
          {stats?.recent?.subscriptions?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent.subscriptions.map((sub, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{sub.name}</p>
                    <p className="text-white/50 text-sm">{sub.email}</p>
                  </div>
                  {sub.subscription_started_at && (
                    <span className="text-white/40 text-xs">
                      {new Date(sub.subscription_started_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white/40 text-center py-4">No recent subscribers</p>
          )}
        </div>

        {/* Recent Projects */}
        <div className="bg-zinc-900 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Layers size={18} className="text-[#00A86B]" />
            Recent Projects
          </h3>
          {stats?.recent?.projects?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent.projects.map((proj, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{proj.title}</p>
                    <p className="text-white/50 text-sm">by {proj.creator?.name || 'Unknown'}</p>
                  </div>
                  {proj.created_at && (
                    <span className="text-white/40 text-xs">
                      {new Date(proj.created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white/40 text-center py-4">No recent projects</p>
          )}
        </div>
      </div>

      {/* Subscription Revenue Estimate */}
      <div className="bg-gradient-to-r from-[#00A86B]/20 to-transparent border border-[#00A86B]/30 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-2">Monthly Recurring Revenue</h3>
        <p className="text-4xl font-bold text-[#00A86B]">
          ${(stats?.users?.active_subscriptions || 0) * 30}
        </p>
        <p className="text-white/60 text-sm mt-1">
          Based on {stats?.users?.active_subscriptions || 0} active subscriptions at $30/month
        </p>
      </div>
    </div>
  );
};

/**
 * Stat Card Component
 */
const StatCard = ({ label, value, icon: Icon, color }) => {
  const colors = {
    green: 'bg-[#00A86B]/20 text-[#00A86B]',
    blue: 'bg-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/20 text-purple-400',
    orange: 'bg-orange-500/20 text-orange-400'
  };

  return (
    <div className="bg-zinc-900 border border-white/10 rounded-xl p-6">
      <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center mb-4`}>
        <Icon size={20} />
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
      <p className="text-white/60 text-sm">{label}</p>
    </div>
  );
};

/**
 * Subscribers Section
 */
const SubscribersSection = ({ subscribers, onRefresh }) => {
  const token = localStorage.getItem('session_token') || localStorage.getItem('auth_token');
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">All Subscribers</h2>
        <p className="text-white/60">{subscribers.length} subscribers</p>
      </div>

      <div className="bg-zinc-900 border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left px-6 py-3 text-white/60 text-sm font-medium">User</th>
              <th className="text-left px-6 py-3 text-white/60 text-sm font-medium">Status</th>
              <th className="text-left px-6 py-3 text-white/60 text-sm font-medium">Plan</th>
              <th className="text-left px-6 py-3 text-white/60 text-sm font-medium">Started</th>
              <th className="text-left px-6 py-3 text-white/60 text-sm font-medium">Profile</th>
            </tr>
          </thead>
          <tbody>
            {subscribers.map((sub) => (
              <tr key={sub.user_id} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-6 py-4">
                  <div>
                    <p className="text-white font-medium">{sub.name}</p>
                    <p className="text-white/50 text-sm">{sub.email}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <StatusBadge status={sub.subscription_status} />
                </td>
                <td className="px-6 py-4 text-white/60">
                  {sub.subscription_plan || 'Creator Monthly'}
                </td>
                <td className="px-6 py-4 text-white/60 text-sm">
                  {sub.subscription_started_at 
                    ? new Date(sub.subscription_started_at).toLocaleDateString()
                    : '-'}
                </td>
                <td className="px-6 py-4">
                  {sub.creator_slug ? (
                    <a
                      href={`/creators/${sub.creator_slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#00A86B] hover:underline text-sm flex items-center gap-1"
                    >
                      View <Eye size={14} />
                    </a>
                  ) : (
                    <span className="text-white/40 text-sm">No profile</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Projects Section
 */
const ProjectsSection = ({ projects, onRefresh }) => {
  const [updating, setUpdating] = useState(null);

  const updateStatus = async (projectId, status) => {
    const token = localStorage.getItem('session_token') || localStorage.getItem('auth_token');
    setUpdating(projectId);

    try {
      const response = await fetch(`${API_URL}/api/btt/admin/projects/${projectId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">All Projects</h2>
        <p className="text-white/60">{projects.length} projects</p>
      </div>

      <div className="bg-zinc-900 border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left px-6 py-3 text-white/60 text-sm font-medium">Project</th>
              <th className="text-left px-6 py-3 text-white/60 text-sm font-medium">Creator</th>
              <th className="text-left px-6 py-3 text-white/60 text-sm font-medium">Stage</th>
              <th className="text-left px-6 py-3 text-white/60 text-sm font-medium">Status</th>
              <th className="text-left px-6 py-3 text-white/60 text-sm font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((proj) => (
              <tr key={proj.project_id} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-6 py-4">
                  <div>
                    <p className="text-white font-medium">{proj.title}</p>
                    <p className="text-white/50 text-sm line-clamp-1">{proj.logline}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <p className="text-white/80">{proj.creator?.name || 'Unknown'}</p>
                  <p className="text-white/40 text-sm">{proj.creator?.email}</p>
                </td>
                <td className="px-6 py-4 text-white/60 text-sm uppercase">
                  {proj.stage}
                </td>
                <td className="px-6 py-4">
                  <StatusBadge status={proj.status} />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <a
                      href={`/projects/${proj.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded text-sm text-white"
                    >
                      View
                    </a>
                    {proj.status === 'active' ? (
                      <button
                        onClick={() => updateStatus(proj.project_id, 'hidden')}
                        disabled={updating === proj.project_id}
                        className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 rounded text-sm text-red-400"
                      >
                        Hide
                      </button>
                    ) : (
                      <button
                        onClick={() => updateStatus(proj.project_id, 'active')}
                        disabled={updating === proj.project_id}
                        className="px-3 py-1 bg-green-500/20 hover:bg-green-500/30 rounded text-sm text-green-400"
                      >
                        Activate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Applications Section
 */
const ApplicationsSection = ({ applications, onRefresh }) => {
  const [updating, setUpdating] = useState(null);

  const updateApplication = async (applicationId, status) => {
    const token = localStorage.getItem('session_token') || localStorage.getItem('auth_token');
    setUpdating(applicationId);

    try {
      const response = await fetch(`${API_URL}/api/applications/${applicationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error updating application:', err);
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Applications</h2>
        <p className="text-white/60">{applications.length} applications</p>
      </div>

      <div className="space-y-4">
        {applications.map((app) => (
          <div key={app.application_id} className="bg-zinc-900 border border-white/10 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-white">{app.name}</h3>
                <p className="text-white/60">{app.email}</p>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={app.status} />
                <span className="text-white/40 text-sm">
                  {new Date(app.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-white/40 text-sm">Program</p>
                <p className="text-white">{app.program || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-white/40 text-sm">Experience</p>
                <p className="text-white">{app.experience_level || 'Not specified'}</p>
              </div>
            </div>

            {app.project_title && (
              <div className="mb-4 p-4 bg-white/5 rounded-lg">
                <p className="text-white/40 text-sm mb-1">Project</p>
                <p className="text-white font-medium">{app.project_title}</p>
                <p className="text-white/60 text-sm">{app.project_logline}</p>
              </div>
            )}

            <div className="mb-4">
              <p className="text-white/40 text-sm mb-1">Why Be The Train?</p>
              <p className="text-white/80 text-sm">{app.why_btt}</p>
            </div>

            {app.status === 'pending' && (
              <div className="flex gap-2 pt-4 border-t border-white/10">
                <button
                  onClick={() => updateApplication(app.application_id, 'accepted')}
                  disabled={updating === app.application_id}
                  className="px-4 py-2 bg-[#00A86B] text-black font-bold rounded hover:bg-[#00A86B]/90"
                >
                  Accept
                </button>
                <button
                  onClick={() => updateApplication(app.application_id, 'reviewing')}
                  disabled={updating === app.application_id}
                  className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30"
                >
                  Mark Reviewing
                </button>
                <button
                  onClick={() => updateApplication(app.application_id, 'declined')}
                  disabled={updating === app.application_id}
                  className="px-4 py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                >
                  Decline
                </button>
              </div>
            )}
          </div>
        ))}

        {applications.length === 0 && (
          <div className="text-center py-12 bg-zinc-900 border border-white/10 rounded-xl">
            <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/40">No applications yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Status Badge Component
 */
const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-[#00A86B]/20 text-[#00A86B]',
    canceled: 'bg-red-500/20 text-red-400',
    past_due: 'bg-orange-500/20 text-orange-400',
    pending: 'bg-yellow-500/20 text-yellow-400',
    reviewing: 'bg-blue-500/20 text-blue-400',
    accepted: 'bg-[#00A86B]/20 text-[#00A86B]',
    declined: 'bg-red-500/20 text-red-400',
    hidden: 'bg-zinc-500/20 text-zinc-400',
    none: 'bg-zinc-500/20 text-zinc-400'
  };

  return (
    <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${styles[status] || styles.none}`}>
      {status}
    </span>
  );
};

export default BTTAdminDashboard;
